import React, { ErrorInfo, ReactNode } from "react";
import { Alert, AlertTitle, Box, Button, Collapse, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportIcon from "@mui/icons-material/BugReport";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * ErrorBoundary — contém erros de render do React em uma subárvore sem deixar
 * a aplicação inteira quebrar (tela branca ou overlay vermelho do dev server).
 *
 * Uso: envolver rotas/painéis sensíveis. Em caso de exceção, mostra uma UI
 * amigável com o erro e um botão "Tentar novamente" que reseta o estado.
 *
 * Exemplo:
 *   <ErrorBoundary scopeName="Emission Plus">
 *     <EmissionEstimator {...} />
 *   </ErrorBoundary>
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  scopeName?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary:${this.props.scopeName || "root"}]`,
      error,
      errorInfo?.componentStack,
    );
    this.props.onError?.(error, errorInfo);
  }

  private reset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  render(): ReactNode {
    if (!this.state.hasError || !this.state.error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);

    const scope = this.props.scopeName || "este módulo";
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          minHeight: 240,
          gap: 2,
          bgcolor: "#FFF",
        }}
      >
        <Alert
          severity="error"
          icon={<BugReportIcon />}
          sx={{ maxWidth: 680, width: "100%", borderRadius: "12px" }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            Algo deu errado ao renderizar {scope}
          </AlertTitle>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 1, color: "#374151" }}>
            {this.state.error.message || "Erro desconhecido durante a renderização."}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748B", display: "block", mb: 1.5 }}>
            Outras áreas do sistema continuam funcionando normalmente. Você pode tentar
            novamente ou recarregar a página.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={this.reset}>
              Tentar novamente
            </Button>
            <Button size="small" variant="outlined" onClick={() => window.location.reload()}>
              Recarregar página
            </Button>
            <Button
              size="small"
              variant="text"
              endIcon={<ExpandMoreIcon sx={{
                transform: this.state.showDetails ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }} />}
              onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
            >
              {this.state.showDetails ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
            </Button>
          </Box>
          <Collapse in={this.state.showDetails}>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", maxHeight: 240, overflow: "auto" }}>
              <Typography variant="caption" component="pre" sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                fontSize: 11,
                color: "#475569",
                m: 0,
              }}>
                {this.state.error.stack || this.state.error.message}
                {this.state.errorInfo?.componentStack ? `\n\n--- Component stack ---${this.state.errorInfo.componentStack}` : ""}
              </Typography>
            </Box>
          </Collapse>
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
